import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { DOMSerializer } from '@tiptap/pm/model'
import { parseHtml, sliceFor, inlinePreview, stripFences } from './aiContent'

// AI edits are proposals until the user accepts them. A change replaces
// the range from..to (from === to for a pure insertion) with `html`; the
// range is mapped through every transaction, so the user can keep editing
// around it. The replaced content is struck through and the proposed
// content is drawn after it as a widget, with Accept / Reject buttons when
// `controls` is set (the inline prompt shows its own while it is open).
//
// The plugin also keeps "anchors": the ranges of the document's top-level
// blocks when a chat turn started, mapped the same way, so the agent's
// changes (which name those blocks by id) land where the agent meant even
// if the document changed while it worked.

export const aiReviewKey = new PluginKey('aiReview')

let changeCounter = 0
export const newChangeId = () => {
  changeCounter += 1
  return `ai-${changeCounter}`
}

const mapChange = (change, mapping) => {
  const from = mapping.map(change.from, 1)
  const to = Math.max(from, mapping.map(change.to, -1))
  if (change.to > change.from && to === from) {
    // The user deleted everything the change was replacing
    return null
  }
  return from === change.from && to === change.to ? change : { ...change, from, to }
}

const mapAnchors = (anchors, mapping) => {
  const mapped = {}
  Object.keys(anchors).forEach((id) => {
    const from = mapping.map(anchors[id].from, 1)
    const to = mapping.map(anchors[id].to, -1)
    if (to > from) {
      mapped[id] = { from, to }
    }
  })
  return mapped
}

const button = (label, className, onClick) => {
  const element = document.createElement('button')
  element.type = 'button'
  element.className = className
  element.textContent = label
  element.addEventListener('mousedown', event => event.preventDefault())
  element.addEventListener('click', onClick)
  return element
}

const renderProposal = (view, change) => {
  const { schema } = view.state
  const inline = view.state.doc.resolve(change.to).parent.inlineContent
  const fragment = parseHtml(schema, stripFences(change.html))
  const preview = inline ? inlinePreview(schema, fragment) : null
  const container = document.createElement(preview ? 'span' : 'div')
  container.className = `ai-proposal ${preview ? 'inline' : 'block'}`
  container.setAttribute('data-ai-change', change.id)
  container.contentEditable = 'false'
  container.appendChild(DOMSerializer.fromSchema(schema).serializeFragment(preview || fragment))
  if (change.status === 'streaming') {
    const caret = document.createElement('span')
    caret.className = 'ai-caret'
    container.appendChild(caret)
  }
  if (change.controls && change.status === 'ready') {
    const controls = document.createElement('span')
    controls.className = 'ai-proposal-controls'
    controls.appendChild(button('Accept', 'accept', () => acceptChanges(view, [change.id])))
    controls.appendChild(button('Reject', 'reject', () => rejectChanges(view, [change.id])))
    container.appendChild(controls)
  }
  return container
}

const buildDecorations = (doc, changes) => {
  const decorations = []
  changes.forEach((change) => {
    if (change.to > change.from) {
      decorations.push(Decoration.inline(change.from, change.to, { class: 'ai-removed' }))
      doc.nodesBetween(change.from, change.to, (node, pos) => {
        if (!node.isBlock || pos < change.from || pos + node.nodeSize > change.to) {
          return true
        }
        const emptyTextblock = node.isTextblock && node.content.size === 0
        decorations.push(Decoration.node(pos, pos + node.nodeSize, {
          class: emptyTextblock ? 'ai-removed-empty' : 'ai-removed-block'
        }))
        return false
      })
    }
    decorations.push(Decoration.widget(change.to, view => renderProposal(view, change), {
      side: -1,
      key: `${change.id}:${change.status}:${change.controls}:${change.html}`,
      ignoreSelection: true,
      stopEvent: () => true
    }))
  })
  return DecorationSet.create(doc, decorations)
}

export const AiReview = Extension.create({
  name: 'aiReview',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: aiReviewKey,
        state: {
          init: () => ({ changes: [], anchors: {}, decorations: DecorationSet.empty }),
          apply: (tr, value) => {
            let { changes, anchors } = value
            if (tr.docChanged) {
              changes = changes.map(change => mapChange(change, tr.mapping)).filter(Boolean)
              anchors = mapAnchors(anchors, tr.mapping)
            }
            const action = tr.getMeta(aiReviewKey)
            if (action && action.set) {
              const others = changes.filter(change => !action.set.some(set => set.id === change.id))
              changes = [...others, ...action.set].sort((a, b) => a.from - b.from)
            }
            if (action && action.remove) {
              changes = changes.filter(change => !action.remove.includes(change.id))
            }
            if (action && action.anchors) {
              ({ anchors } = action)
            }
            if (changes === value.changes && anchors === value.anchors) {
              return value
            }
            const decorations = changes === value.changes ?
              value.decorations :
              buildDecorations(tr.doc, changes)
            return { changes, anchors, decorations }
          }
        },
        props: {
          decorations: state => aiReviewKey.getState(state).decorations
        }
      })
    ]
  }
})

const dispatchMeta = (view, meta) => {
  view.dispatch(view.state.tr.setMeta(aiReviewKey, meta).setMeta('addToHistory', false))
}

export const getChanges = view => aiReviewKey.getState(view.state).changes

export const getChange = (view, id) => getChanges(view).find(change => change.id === id)

// Adds or updates changes: { id, from, to, html, status: 'streaming' | 'ready', controls }
export const setChanges = (view, changes) => {
  const current = getChanges(view)
  const merged = changes.map(change => ({ ...current.find(existing => existing.id === change.id), ...change }))
  dispatchMeta(view, { set: merged })
}

export const rejectChanges = (view, ids) => dispatchMeta(view, { remove: ids })

// Applies the changes in one undoable transaction
export const acceptChanges = (view, ids) => {
  const { state } = view
  const changes = getChanges(view).filter(change => ids.includes(change.id) && change.status === 'ready')
  const { tr } = state
  changes.forEach((change) => {
    const from = tr.mapping.map(change.from, 1)
    const to = Math.max(from, tr.mapping.map(change.to, -1))
    const fragment = parseHtml(state.schema, stripFences(change.html))
    tr.replace(from, to, sliceFor(tr.doc.resolve(from), tr.doc.resolve(to), fragment))
  })
  tr.setMeta(aiReviewKey, { remove: ids })
  view.dispatch(tr.scrollIntoView())
  view.focus()
}

export const setAnchors = (view, blocks) => {
  const anchors = {}
  blocks.forEach((block) => {
    anchors[block.id] = { from: block.from, to: block.to }
  })
  dispatchMeta(view, { anchors })
}

// Turns the agent's operations on block ids into changes. Operations on
// blocks the user has since deleted are dropped. Returns the new change ids.
export const addAgentChanges = (view, operations) => {
  const { anchors } = aiReviewKey.getState(view.state)
  const changes = []
  operations.forEach((operation) => {
    let range = null
    if (operation.op === 'insert') {
      const after = operation.after_id && anchors[operation.after_id]
      if (!operation.after_id || after) {
        range = { from: after ? after.to : 0, to: after ? after.to : 0 }
      }
    } else if (anchors[operation.from_id] && anchors[operation.to_id]) {
      range = { from: anchors[operation.from_id].from, to: anchors[operation.to_id].to }
    }
    if (range && range.to >= range.from) {
      changes.push({ id: newChangeId(), ...range, html: operation.html || '', status: 'ready', controls: true })
    }
  })
  dispatchMeta(view, { set: changes, anchors: {} })
  return changes.map(change => change.id)
}
